import React, { useState, useEffect, useMemo } from 'react';
import 'react-table-6/react-table.css';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col, Badge } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from "react-router-dom";
import Config from '../../../config/Config';
import { Utils, Filters } from '../../../services';
import { ButtonGroup, Button } from "reactstrap";
import { PageTitle, Ability, Table, TableButtons } from '../../../components';
import ApplicationsFilters from './ApplicationFilters/ApplicationsFilters';
import { Application } from '../../../models';
import {
    loadApplications, loadApplicationTypes,
    acceptApplication, rejectApplication,
    loadSectors, updateApplicationsFilters,
    setMessagesRecipients
} from '../../../redux';
import _ from 'lodash';
import moment from 'moment';


import './ApplicationsList.css';
import user_avatar from '../../../assets/images/user_avatar.jpg';

const ApplicationsList = () => {
    const { t, i18n }  = useTranslation();
    const dispatch     = useDispatch();
    const history      = useHistory();
    const default_lang = Utils.default_lang();

    const [selection, setSelection] = useState([]);

    const { user }                             = useSelector(state => state.auth);
    const { sectors }                          = useSelector(state => state.sectors);
    const { isMobile, isTablet }               = useSelector(state => state.ui);
    const { application_types }                = useSelector(state => state.applicationTypes);
    const { applications, isLoading, filters } = useSelector(state => state.applications);

    const isResponsive = isMobile || isTablet;

    useEffect(() => {
        dispatch(loadApplicationTypes());
        dispatch(loadSectors());
    }, [dispatch]);

    useEffect(() => {
        dispatch(loadApplications(filters));
    }, [dispatch, filters])

    const submit = (values) => {
        const payload = _.cloneDeep(values || {});
        dispatch(updateApplicationsFilters(payload));
    }

    const accept = id => dispatch(acceptApplication(id));
    const reject = id => dispatch(rejectApplication(id));

    const title = <span>{t("applications.applications_plural")}</span>;

    const exportLink = React.useMemo(() => {
        const link = new URL(`${Config.api_end_point}/application/export`);

        link.searchParams.append('x-api-key', user.api_key);
        link.searchParams.append('lang', i18n.language);

        if (filters?.search?.length)
            link.searchParams.append('search', filters.search);
        if (filters.having_no_shift === true)
            link.searchParams.append('having_no_shift', true);
        if (filters.having_shift === true)
            link.searchParams.append('having_shift', true);

        if (filters?.types?.length)
            filters.types.forEach(t => link.searchParams.append('types[]', t));
        if (filters?.status?.length)
            filters.status.forEach(s => link.searchParams.append('status[]', s));
        if (filters?.sectors?.length)
            filters.sectors.forEach(s => link.searchParams.append('sectors[]', s));
        if (filters?.having_shift_in_sectors?.length)
            filters.having_shift_in_sectors.forEach(s => link.searchParams.append('having_shift_in_sectors[]', s));


        return link.href;
    }, [filters], i18n.language, user);

    const actions = (
        <>
            {(user?.can && user.can("read", "applications")) &&
                <ButtonGroup size="sm" className={`mr-3 ${isResponsive ? " w-100" : ""}`}>
                    <a target="_blank" href={ exportLink } className="btn btn-primary btn-sm" >
                        <i className="fa fa-download"></i>&nbsp;{t("applications.export")}
                    </a>
                </ButtonGroup>
            }
            <Ability can="applications:create">
                { application_types?.map(type =>
                    <Button
                        to={`/public/${user?.tkt_instance.slug}/application/${type.id}`}
                        key={type.id}
                        tag={Link}
                        size="sm"
                        color="info"
                        title={t("applications.public_form")}
                        target="_blank"
                        className="ml-2"
                    >
                        <i className="fa fa-plus"></i>&nbsp;
                        { Utils.localized_or_fallback(type.name, i18n.lang) }
                    </Button>
                )}
            </Ability>
        </>
    );

    const applicationsToShow = applications?.map(app => ({
        ...app,
        fullname: app?.user?.fullname
    }));

    const isSelected = React.useCallback(_id => selection.includes(_id), [ selection ]);
    const onChangedCheckbox = _id => {
        if (isSelected(_id))
            setSelection(selection.filter(i => i !== _id));
        else
            setSelection([...selection, _id]);
    };
    const selectAll = React.useCallback(all => {
        setSelection(all ?
            applicationsToShow?.map(t => t.id) :
            []
        );
    }, [applicationsToShow]);
    const allSelected = useMemo(() => {
        if (!applicationsToShow?.length || !selection?.length)
            return false;

        if (applicationsToShow.length !== selection?.length)
            return false;

        return applicationsToShow.reduce(
            (allSelected, application) => allSelected && selection.includes(application.id),
            true
        );
    }, [applicationsToShow, selection]);

    const sendBatchEmail = () => {
        const recipients = selection?.map(id => {
            const application = applications?.find(a => a.id === id);
            if (!application?.user?.email)
                return null;

            return {
                to: {
                    name: [
                        application.user?.firstname,
                        application.user?.lastname
                    ].filter(Boolean).join(' ') || application.user?.email,
                    address: application.user.email
                },
                context: {
                    application: {
                        id: application.id,
                        user: {
                            id: application.user?.id,
                            firstname: application.user?.firstname,
                            lastname: application.user?.lastname,
                            email: application.user?.email
                        }
                    }
                }
            };
        }).filter(Boolean);
        dispatch(setMessagesRecipients(recipients));
        history.push('/messages');
    }

    const columns = [{
        Header: cell => (
            <input
                className="checkbox"
                type="checkbox"
                checked={!!allSelected}
                onChange={() => selectAll(!allSelected)}
                value={cell.value}
            />
        ),
        filterable: false,
        sortable: false,
        accessor: 'id',
        maxWidth: 90,
        className: "checkbox-cell",
        Cell: cell => (
            <input
                className="checkbox"
                type="checkbox"
                checked={isSelected(cell.value)}
                onChange={() => onChangedCheckbox(cell.value)} value={cell.value}
            />
        )
    }, {
        Header: t('applications.fullname'),
        accessor: "fullname",
        filterMethod: Filters.textFilterMethod('fullname'),
        Filter: Filters.textFilter,
        filterAll: true,
        Cell: row => row.value ? (
            <div className="user_cell">
                <div className="img_wrapper">
                    <img src={row.original.user.avatar || user_avatar} />
                </div>
                <div>
                    <Link to={`/users/edit/${row.original.user.id}#team`}>
                        <span className="fullname">{row.original.user?.fullname}</span>
                        <span className="email">{row.original.user?.email}</span>
                    </Link>
                </div>
            </div>
        ) : null
    }, {
        Header: t('applications.max_nb_hours_header'),
        accessor: "max_nb_hours",
        filterMethod: Filters.textFilterMethod("max_nb_hours"),
        Filter: Filters.textFilter,
        filterAll: true,
        Cell: row => (row.value >= 0) ? (
            <h4 className="text-center w-100">
                <Badge color="dark">
                    { row.value }h / { row.original.max_nb_hours_per_day }h
                </Badge>
            </h4>
        ) : null
    }, {
        Header: t('applications.nb_hours'),
        accessor: "nb_hours",
        filterMethod: Filters.textFilterMethod("nb_hours"),
        Filter: Filters.textFilter,
        filterAll: true,
        Cell: row => (row.value >= 0) ? (
            <h4 className="text-center w-100">
                <Badge color="dark">
                    { row.value.toFixed(2) } h
                </Badge>
            </h4>
        ) : null
    }, {
        Header: t('applications.busy_rate'),
        accessor: "busy_rate",
        filterMethod: Filters.textFilterMethod("busy_rate"),
        Filter: Filters.textFilter,
        filterAll: true,
        Cell: row => (row.value >= 0) ? (
            <h4 className="text-center w-100">
                <Badge color={row.value >= 100 ? 'danger' : (row.value >= 75 ? 'warning' : (row.value > 0 ? 'success' : 'info'))}>
                    { row.value.toFixed(2) } %
                </Badge>
            </h4>
        ) : null
    }, {
        Header: t('applications.sectors'),
        accessor: "sectors",
        filterMethod: Filters.sectorsFilterMethod,
        Filter: Filters.sectorsFilter(sectors),
        filterAll: true,
        Cell: row => row.value ? (
            <div className="text-center d-flex flex-wrap">
                { row.value?.map(sector => (
                    <div key={sector.id} className="badge badge-light m-1"> {
                        sector.name[i18n.language]}
                    </div>
                ))}
            </div>
        ) : null
    }, {
        Header: t('applications.created_at'),
        accessor: "created_at",
        filterMethod: Filters.textFilterMethod("created_at"),
        Filter: Filters.textFilter,
        filterAll: true,
        Cell: row => (
            <div className="dates">
                <span className="date">
                    <i className="fa fa-calendar"></i>&nbsp;
                    {moment(row.value).format('LLL')}
                </span>
            </div>
        )
    }, {
        Header: t('applications.status'),
        accessor: "status",
        filterMethod: Filters.textFilterMethod("status"),
        Filter: Filters.textFilter,
        filterAll: true,
        Cell: row => row.value ? (
            <div className="text-center">
                {row.value === Application.STATUS_SUBMITTED && (
                    <div className="badge badge-info py-2 px-4">
                        { t('applications.status_submitted') }
                    </div>
                )}
                {row.value === Application.STATUS_ACCEPTED && (
                    <div className="badge badge-success py-2 px-4">
                        { t('applications.status_accepted') }
                    </div>
                )}
                {row.value === Application.STATUS_REJECTED && (
                    <div className="badge badge-danger py-2 px-4">
                        { t('applications.status_rejected') }
                    </div>
                )}
            </div>
        ) : null
    }, {
        Header: t('common.actions'),
        accessor: "id",
        maxWidth: 110,
        filterable: false,
        Cell: row => (
            <div>
                { isLoading ?
                    <div className="d-flex">
                        <i className="fa fa-spinner fa-spin"></i>
                    </div>
                    :
                    <>
                        <ButtonGroup>
                            <Ability can="applications:read">
                                <Button tag={Link} to={`/users/edit/${row.original.user.id}#team`} color="primary" title={t("common.see")} size="sm">
                                    <i className="fa fa-eye"></i>
                                </Button>
                            </Ability>
                            <Ability can="applications:delete">
                                <Button disabled={row.original.nb_hours > 0} tag={Link} to={`/applications/delete/${row.value}`} color="danger" title={t("common.delete")} size="sm">
                                    <i className="fa fa-trash"></i>
                                </Button>
                            </Ability>
                        </ButtonGroup>
                        <div className="mt-1">
                            {row.original.status === Application.STATUS_SUBMITTED && (
                                <ButtonGroup>
                                    <Button color="success" onClick={() => accept(row.value)} title={t('applications.accept_this_application')} size="sm">
                                        <i className="fa fa-check"></i>
                                    </Button>
                                    <Button color="danger" onClick={() => reject(row.value)} title={t('applications.reject_this_application')} size="sm">
                                        <i className="fa fa-times"></i>
                                    </Button>
                                </ButtonGroup>
                            )}
                            {row.original.status === Application.STATUS_ACCEPTED && (
                                <Button color="danger" onClick={() => reject(row.value)} title={t('applications.reject_this_application')} size="sm">
                                    <i className="fa fa-times"></i>
                                </Button>
                            )}
                            {row.original.status === Application.STATUS_REJECTED && (
                                <Button color="success" onClick={() => accept(row.value)} title={t('applications.accept_this_application')} size="sm">
                                    <i className="fa fa-check"></i>
                                </Button>
                            )}
                        </div>
                    </>
                }
            </div>
        )
    }];

    return (
        <div className="ApplicationsList">
            <PageTitle icon="address-book-o" title={title} actions={actions} />
            <ApplicationsFilters
                application_types={application_types}
                sectors={(sectors?.length > 0 ? sectors : []).toSorted((a, b) => a.name[i18n.language]?.localeCompare(b.name[i18n.language]))}
                onSubmit={submit}
                isLoading={isLoading}
            />
            {selection?.length > 0 && (
                <div className="border rounded d-flex p-1 mb-1 justify-start">
                    <Button onClick={ sendBatchEmail } color="primary">
                        <i className="fa fa-envelope" />&nbsp;
                        { t('tickets.batch.send_email') }
                    </Button>
                </div>
            )}
            <Table id="applicationsTable" data={applicationsToShow} columns={columns} />
        </div>
    );
}

export default ApplicationsList;
